import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import EmptyRows from "../../../../components/reusableComponent/EmptyRows";
import CustomDate from "../../../../components/common/CustomDate";
import TableModal from "../../../../components/reusableComponent/TableModal";
import {
  useGetTicketClickToExpandQuery,
  useTicketDashboardQuery,
} from "../../../../Services/dashboard/TicketDashboard";

interface IProps {
  data: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: string | null;
}

const PRIORITY_ORDER = ["Urgent", "High", "Medium", "Low"];

const PRIORITY_COLORS: Record<string, { dark: string; light: string }> = {
  Urgent: { dark: "#dc2626", light: "#fca5a5" },
  High: { dark: "#ea580c", light: "#fdba74" },
  Medium: { dark: "#2563eb", light: "#93c5fd" },
  Low: { dark: "#059669", light: "#6ee7b7" },
};

const expandButtonStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
  color: "white",
  border: "none",
  borderRadius: "10px",
  padding: "12px 20px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 600,
  transition: "all 0.3s",
  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatApiDateToDisplay(dateStr?: string): string {
  if (!dateStr || dateStr.length < 10) return "";
  const [y, m, d] = dateStr.slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

const DateRangeSubtitle = ({
  start,
  end,
}: {
  start?: string;
  end?: string;
}) => {
  const startLabel = formatApiDateToDisplay(start);
  const endLabel = formatApiDateToDisplay(end);
  if (!startLabel || !endLabel) return null;

  return (
    <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
      {startLabel} – {endLabel}
    </div>
  );
};

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return { startDate, endDate, key: "selection" as const };
}

const parseCount = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizePriority = (value: string) => {
  const key = value.trim().toLowerCase();
  if (key === "urgent" || key === "uragent") return "Urgent";
  if (key === "high") return "High";
  if (key === "medium") return "Medium";
  if (key === "low") return "Low";
  return "";
};

const priorityToApiValue = (priority: string) => priority.trim().toLowerCase();

const formatTicketCreated = (raw: unknown) => {
  if (!raw) return "—";
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
};

const TICKET_CLICK_TO_EXPAND_COLUMNS = [
  {
    label: "Ticket ID",
    render: (row: any) => row?.ticket_id ?? row?.id ?? "—",
  },
  { label: "Title", render: (row: any) => row?.title ?? "—" },
  { label: "Status Type", render: (row: any) => row?.status_type ?? "—" },
  {
    label: "Contact Name",
    render: (row: any) => row?.contact_name ?? "—",
  },
  {
    label: "Contact Email",
    render: (row: any) => row?.contact_email ?? "—",
  },
  {
    label: "Contact Phone",
    render: (row: any) => row?.contact_phone ?? "—",
  },
  {
    label: "Created",
    render: (row: any) => formatTicketCreated(row?.created),
  },
];

const addPriorityCountsFromItem = (
  item: Record<string, unknown>,
  buckets: Record<string, { withinSL: number; breach: number }>,
) => {
  Object.entries(item).forEach(([key, counts]) => {
    const priority = normalizePriority(key);
    if (!priority || typeof counts !== "object" || counts == null) return;

    const values = counts as Record<string, unknown>;
    buckets[priority].withinSL += parseCount(
      values.disposition_sl_resolved ??
        values.within_sla ??
        values.within_sl ??
        values.within,
    );
    buckets[priority].breach += parseCount(
      values.disposition_sl_breached ??
        values.breach ??
        values.breached_sla ??
        values.without_sla ??
        values.breached,
    );
  });
};

const buildPriorityChartData = (data: any) => {
  const buckets: Record<string, { withinSL: number; breach: number }> = {
    Urgent: { withinSL: 0, breach: 0 },
    High: { withinSL: 0, breach: 0 },
    Medium: { withinSL: 0, breach: 0 },
    Low: { withinSL: 0, breach: 0 },
  };

  const dispositionGraph = data?.disposition_sl_graph;
  const hasDispositionGraph =
    Array.isArray(dispositionGraph) && dispositionGraph.length > 0;

  if (hasDispositionGraph) {
    dispositionGraph.forEach((row: any) => {
      const dateEntry = Object.entries(row ?? {}).find(([key]) =>
        /^\d{4}-\d{2}-\d{2}$/.test(key),
      );

      if (dateEntry) {
        const [, items] = dateEntry;
        if (Array.isArray(items)) {
          items.forEach((item: any) => addPriorityCountsFromItem(item, buckets));
        }
        return;
      }

      if (row?.priority_counts && typeof row.priority_counts === "object") {
        addPriorityCountsFromItem(row.priority_counts, buckets);
        return;
      }

      const hasPriorityKeys = Object.keys(row ?? {}).some((key) =>
        Boolean(normalizePriority(key)),
      );
      if (hasPriorityKeys) {
        addPriorityCountsFromItem(row, buckets);
      }
    });
  } else {
    const apiArrays = [
      data?.priority_resolution_sla,
      data?.priority_wise_resolution_sla,
      data?.sla_by_priority,
      data?.breach_count_by_priority,
      data?.seven_day_priority_breach,
      data?.priority_based_resolution_sla,
    ].filter((item) => Array.isArray(item) && item.length > 0) as any[];

    if (apiArrays.length > 0) {
      apiArrays[0].forEach((row: any) => {
        const priority = normalizePriority(
          String(
            row?.priority ??
              row?.priority_name ??
              row?.name ??
              row?.label ??
              "",
          ),
        );
        if (!priority) return;

        buckets[priority].withinSL += parseCount(
          row?.within_sla ?? row?.within_sl ?? row?.withinSL ?? row?.within,
        );
        buckets[priority].breach += parseCount(
          row?.breach ??
            row?.breached_sla ??
            row?.breach_count ??
            row?.without_sla ??
            row?.breached,
        );
      });
    }
  }

  const hasApiSource =
    hasDispositionGraph ||
    [
      data?.priority_resolution_sla,
      data?.priority_wise_resolution_sla,
      data?.sla_by_priority,
      data?.breach_count_by_priority,
      data?.seven_day_priority_breach,
      data?.priority_based_resolution_sla,
    ].some((item) => Array.isArray(item) && item.length > 0);

  const rows = PRIORITY_ORDER.map((priority) => ({
    priority,
    withinSL: buckets[priority].withinSL,
    breach: buckets[priority].breach,
    total: buckets[priority].withinSL + buckets[priority].breach,
  }));

  const chartRows = rows.sort((a, b) => b.breach - a.breach);

  const hasValues = chartRows.some(
    (row) => row.withinSL > 0 || row.breach > 0,
  );

  return { chartRows, hasValues, hasChartStructure: hasApiSource };
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const entry =
    payload.find((item: any) => parseCount(item?.value) > 0) ?? payload[0];
  if (!entry || parseCount(entry?.value) <= 0) return null;

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "10px 12px",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
        fontSize: 12,
        minWidth: 160,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: "#1e293b",
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        Priority: {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor: entry.color,
          }}
        />
        <span>{entry.name}</span>
        <strong style={{ marginLeft: "auto", color: "#1e293b" }}>
          {entry.value}
        </strong>
      </div>
    </div>
  );
};

const PriorityLegend = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "16px 12px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        justifyContent: "center",
      }}
    >
      {PRIORITY_ORDER.map((priority) => {
        const colors = PRIORITY_COLORS[priority];
        return (
          <div
            key={priority}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${colors.dark}40`,
              backgroundColor: "#ffffff",
            }}
          >
            <div style={{ display: "flex", gap: "3px" }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: colors.dark,
                }}
              />
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: colors.light,
                }}
              />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
              {priority}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const PriorityBasedResolutionSLA: React.FC<IProps> = ({
  data,
  startDate,
  endDate,
  teamId,
  workflowId,
}) => {
  const navigate = useNavigate();
  const [hoveredCell, setHoveredCell] = useState<{
    index: number;
    dataKey: string;
  } | null>(null);
  const [fullscreenChart, setFullscreenChart] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);
  const [selectedBarClick, setSelectedBarClick] = useState<{
    priority: string;
    priorityLabel: string;
    value: string;
    valueLabel: string;
  } | null>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange],
  );

  const { data: modalPerformanceData } = useTicketDashboardQuery(
    {
      dashboardType: "performance",
      selectedTeam: teamId ?? "",
      selectedWorkFlow: workflowId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    },
  );

  const { chartRows, hasChartStructure } = useMemo(
    () => buildPriorityChartData(data),
    [data],
  );

  const modalChartResult = useMemo(() => {
    const source =
      fullscreenChart && modalPerformanceData != null
        ? modalPerformanceData
        : data;
    return buildPriorityChartData(source);
  }, [fullscreenChart, modalPerformanceData, data]);

  const modalChartRows = modalChartResult.chartRows;
  const modalHasChartStructure = modalChartResult.hasChartStructure;

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate ?? "";
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate ?? "";

  const clickToExpandParams = useMemo(() => {
    if (
      !tableModalOpen ||
      !selectedBarClick ||
      !teamId ||
      !expandStart ||
      !expandEnd
    ) {
      return null;
    }

    return {
      graph_name: "disposition_sl_graph",
      value: selectedBarClick.value,
      start_date: expandStart,
      end_date: expandEnd,
      team_id: teamId,
      page: clickToExpandPage,
      page_size: 20,
      priority: selectedBarClick.priority,
      ...(workflowId ? { workflow_id: workflowId } : {}),
    };
  }, [
    tableModalOpen,
    selectedBarClick,
    teamId,
    expandStart,
    expandEnd,
    clickToExpandPage,
    workflowId,
  ]);

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 20;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    setClickToExpandPage(1);
  }, [selectedBarClick?.priority, selectedBarClick?.value]);

  const clickToExpandEnabled = Boolean(teamId && expandStart && expandEnd);

  const handleBarCellClick = useCallback(
    (row: any, dataKey: "withinSL" | "breach") => {
      if (!clickToExpandEnabled) return;

      const count = parseCount(dataKey === "withinSL" ? row.withinSL : row.breach);
      if (count <= 0) return;

      setSelectedBarClick({
        priority: priorityToApiValue(row.priority),
        priorityLabel: row.priority,
        value: dataKey === "withinSL" ? "resolved" : "breached",
        valueLabel: dataKey === "withinSL" ? "Within SL" : "Breached",
      });
      setTableModalOpen(true);
      setClickToExpandPage(1);
    },
    [clickToExpandEnabled],
  );

  const handleCloseTableModal = () => {
    setTableModalOpen(false);
    setSelectedBarClick(null);
    setClickToExpandPage(1);
  };

  const getCellStyle = useCallback(
    (index: number, dataKey: string, baseColor: string, row: any) => {
      const isHovered =
        hoveredCell?.index === index && hoveredCell?.dataKey === dataKey;
      const count = parseCount(dataKey === "withinSL" ? row.withinSL : row.breach);

      return {
        fill: baseColor,
        fillOpacity: 1,
        stroke: isHovered ? baseColor : "transparent",
        strokeWidth: isHovered ? 2 : 0,
        style: {
          cursor:
            clickToExpandEnabled && count > 0 ? "pointer" : "default",
        },
      };
    },
    [hoveredCell, clickToExpandEnabled],
  );

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const CellContainer = Cell as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const CartesianGridContainer = CartesianGrid as any;
  const LegendContainer = Legend as any;

  const renderChart = (rows: any[], height: number | string) => (
    <ResponsiveChartContainer width="100%" height={height}>
      <BarChartContainer
        layout="vertical"
        data={rows}
        margin={{ top: 12, right: 24, left: 8, bottom: 8 }}
        barCategoryGap="28%"
        barGap={6}
        onMouseLeave={() => setHoveredCell(null)}
      >
        <CartesianGridContainer
          strokeDasharray="3 3"
          stroke="#e2e8f0"
          horizontal={false}
        />
        <XAxisContainer
          type="number"
          tick={{ fontSize: 11, fill: "#64748b", fontWeight: 500 }}
          tickLine={false}
          axisLine={{ stroke: "#cbd5e1" }}
          allowDecimals={false}
        />
        <YAxisContainer
          type="category"
          dataKey="priority"
          width={90}
          tick={{ fontSize: 12, fill: "#475569", fontWeight: 600 }}
          tickLine={false}
          axisLine={{ stroke: "#cbd5e1" }}
        />
        <TooltipContainer
          content={<CustomTooltip />}
          shared={false}
          cursor={false}
        />
        <LegendContainer verticalAlign="bottom" content={<PriorityLegend />} />
        <BarContainer
          dataKey="withinSL"
          name="Within SL"
          radius={[0, 4, 4, 0]}
          barSize={14}
          isAnimationActive={false}
        >
          {rows.map((row, index) => (
            <CellContainer
              key={`within-${row.priority}`}
              {...getCellStyle(
                index,
                "withinSL",
                PRIORITY_COLORS[row.priority].dark,
                row,
              )}
              onMouseEnter={() =>
                setHoveredCell({ index, dataKey: "withinSL" })
              }
              onClick={() => handleBarCellClick(row, "withinSL")}
            />
          ))}
        </BarContainer>
        <BarContainer
          dataKey="breach"
          name="Breach"
          radius={[0, 4, 4, 0]}
          barSize={14}
          isAnimationActive={false}
        >
          {rows.map((row, index) => (
            <CellContainer
              key={`breach-${row.priority}`}
              {...getCellStyle(
                index,
                "breach",
                PRIORITY_COLORS[row.priority].light,
                row,
              )}
              onMouseEnter={() => setHoveredCell({ index, dataKey: "breach" })}
              onClick={() => handleBarCellClick(row, "breach")}
            />
          ))}
        </BarContainer>
      </BarChartContainer>
    </ResponsiveChartContainer>
  );

  return (
    <>
      <div className="card card-flush border-0 mb-2">
        <div className="d-flex align-items-center justify-content-between card-header w-100">
          <div>
            <span className="fs-2 fw-bold text-gray-800 me-2 lh-1 ls-n2">
              Priority based SL
            </span>
            {hasChartStructure && (
              <DateRangeSubtitle start={startDate} end={endDate} />
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (endDate) {
                setModalDateRange([buildExpandViewDateRange(endDate)]);
              }
              setFullscreenChart(true);
            }}
            style={expandButtonStyle}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
            Expand View
          </button>
        </div>

        <div className="card-body pt-1 px-3 pb-3">
          {!hasChartStructure ? (
            <div
              style={{
                height: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmptyRows value="priority based SL" />
            </div>
          ) : (
            renderChart(chartRows, 360)
          )}
        </div>
      </div>

      {fullscreenChart &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Priority based SL expand view"
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              zIndex: 10000,
              padding: "24px",
              overflow: "auto",
            }}
            onClick={() => setFullscreenChart(false)}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "20px",
                width: "95%",
                maxWidth: "1600px",
                margin: "0 auto",
                padding: "32px",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "28px",
                  paddingBottom: "20px",
                  borderBottom: "2px solid #e2e8f0",
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                <div>
                  <h2 style={{ margin: 0, color: "#1e293b", fontWeight: 700 }}>
                    Priority based SL
                  </h2>
                  <DateRangeSubtitle
                    start={modalRangeStrings.start}
                    end={modalRangeStrings.end}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ position: "relative", zIndex: 10002 }}>
                    <CustomDate
                      dateRange={modalDateRange}
                      setDateRange={setModalDateRange}
                      unrestricted
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setFullscreenChart(false)}
                    style={{
                      background:
                        "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: 44,
                      height: 44,
                      fontSize: 24,
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div
                style={{
                  height: 520,
                  backgroundColor: "#fafbfc",
                  padding: 24,
                  borderRadius: 16,
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {modalHasChartStructure ? (
                  <div style={{ width: "100%", height: "100%" }}>
                    {renderChart(modalChartRows, "100%")}
                  </div>
                ) : (
                  <EmptyRows value="priority based SL" />
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseTableModal}
        title={
          selectedBarClick
            ? `${selectedBarClick.priorityLabel} – ${selectedBarClick.valueLabel}`
            : "Tickets"
        }
        contentLabel="Priority based SL click-to-expand"
        emptyMessage="No tickets found for this selection."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseTableModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={TICKET_CLICK_TO_EXPAND_COLUMNS}
      />
    </>
  );
};

export default PriorityBasedResolutionSLA;
